import { View } from "@animech/camera";
import { BoundingBox, CameraComponent, math, Quat, Vec3 } from "playcanvas";

export function orthoHeight(view: View, orbitCenter: Vec3): number {
	return orthoHeightFromDistance(
		view.position.clone().sub(orbitCenter).length(),
		view.fov,
	);
}

export function orthoHeightFromDistance(distance: number, fov: number): number {
	return distance * Math.tan(fov * 0.5 * math.DEG_TO_RAD);
}

export function perspectiveDistanceFromRadius(
	radius: number,
	fov: number,
): number {
	return radius / Math.sin(fov * 0.5 * math.DEG_TO_RAD);
}

export function horizontalFovFromVerticalFov(
	verticalFov: number,
	aspectRatio: number,
): number {
	return (
		2 *
		Math.atan(Math.tan(verticalFov * 0.5 * math.DEG_TO_RAD) * aspectRatio) *
		math.RAD_TO_DEG
	);
}

export function verticalFovFromHorizontalFov(
	horizontalFov: number,
	aspectRatio: number,
): number {
	return (
		2 *
		Math.atan(Math.tan(horizontalFov * 0.5 * math.DEG_TO_RAD) / aspectRatio) *
		math.RAD_TO_DEG
	);
}

export function viewsAreEqual(
	view1: View,
	view2: View,
	distanceTolerance = 1e-6,
	rotationTolerance = 1e-9,
): boolean {
	return (
		view1.fov === view2.fov &&
		view1.projection === view2.projection &&
		view1.position.distance(view2.position) <= distanceTolerance &&
		view1.rotation.equalsApprox(view2.rotation, rotationTolerance)
	);
}

// See: https://gamedev.stackexchange.com/a/68184
export function relativeQuatRotation(q0: Quat, q1: Quat): Quat {
	return q0.clone().conjugate().mul(q1);
}

export function relativeQuadRotationMagnitude(q0: Quat, q1: Quat): number {
	return relativeQuatRotation(q0, q1).getEulerAngles().length();
}

/**
 * The camera's FOV is locked to one axis (the narrow side of the screen).
 * The other axis gets its FOV derived from the aspect ratio.
 * This function returns both, regardless of which one the camera locks.
 */
function resolveFieldOfView(
	fov: number,
	camera: CameraComponent,
): { hFov: number; vFov: number } {
	return camera.horizontalFov
		? { hFov: fov, vFov: verticalFovFromHorizontalFov(fov, camera.aspectRatio) }
		: {
				hFov: horizontalFovFromVerticalFov(fov, camera.aspectRatio),
				vFov: fov,
			};
}

export function viewWithFocus(
	view: View,
	target: BoundingBox,
	autoZoomMultiplier: number | undefined,
	camera: CameraComponent,
	currentOrbitDistance: number,
): View {
	const { center, halfExtents } = target;
	const radius = halfExtents.length();

	const { hFov, vFov } = resolveFieldOfView(view.fov, camera);

	const focusDistance =
		autoZoomMultiplier !== undefined
			? Math.max(
					perspectiveDistanceFromRadius(radius, vFov),
					perspectiveDistanceFromRadius(radius, hFov),
				) * autoZoomMultiplier
			: currentOrbitDistance;

	return view.setPosition(
		view.rotation
			.transformVector(Vec3.BACK)
			.mulScalar(focusDistance)
			.add(center),
	);
}
